import PropTypes from 'prop-types';

import {Grid, GridItem, Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {HeroBannerBackgroundMedia} from '@amblique/king-living/app/components/hero-banner-background-media';
import {
  hasHeroBannerBackgroundMedia,
  hasHeroBannerVideo,
  HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS,
} from '@amblique/king-living/app/utils/hero-banner-video';

/**
 * Callout Block - Split (CF-023)
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"root" | "grid" | "mediaRegion" | "contentRegion" | "body" | "cta"} HeroBannerParts
 * @typedef {Partial<Record<HeroBannerParts, SystemStyleObject>>} HeroBannerStyles
 *
 * @param {Object} props - Component props
 * @param {Contentful.HeroBanner} props.component - HeroBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {HeroBannerStyles} props.styles - Theme multi-style parts from useMultiStyleConfig
 * @returns {React.ReactElement}
 */
export const CalloutBlockSplitCF023 = ({component, isComplete = true, isFetching = false, styles, ...props}) => {
  const {backgroundMedia, backgroundVideoMobile, body, cta} = component;
  const mediaFields = {backgroundMedia, backgroundVideoMobile};
  const hasBackgroundMedia = hasHeroBannerBackgroundMedia(mediaFields);
  const hasVideo = hasHeroBannerVideo(mediaFields);
  // CF-023: with-content layout when linked `body` and/or `cta` (not headingTitle).
  const hasContent = Boolean(body || cta);

  const gridClassName = [
    'hero-banner',
    !hasContent && 'hero-banner--cf023-no-content',
    hasContent && 'hero-banner--cf023-with-content',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Grid className={gridClassName} {...styles.grid}>
      <GridItem className="hero-content-wrapper" {...styles.contentRegion}>
        {!isComplete && isFetching ? (
          <Skeleton height="80px" />
        ) : (
          <>
            {body && <Component component={body} {...styles.body} />}
            {cta && <Component component={cta} {...styles.cta} />}
          </>
        )}
      </GridItem>

      <GridItem className="hero-image-wrapper" {...styles.mediaRegion}>
        {hasBackgroundMedia ? (
          <HeroBannerBackgroundMedia
            backgroundMedia={backgroundMedia}
            backgroundVideoMobile={backgroundVideoMobile}
            mobileVideoOptions={hasVideo ? HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS : undefined}
            {...styles.media}
          />
        ) : (
          <Skeleton height="80px" />
        )}
      </GridItem>
    </Grid>
  );
};

CalloutBlockSplitCF023.displayName = 'CalloutBlockSplitCF023';
CalloutBlockSplitCF023.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
